export interface CourseModule {
  title: string;
  topics: string[];
}

export type CourseCategory = 'desarrollo-tecnico' | 'posicionamiento' | 'practica' | 'bootcamp';

export const courseCategories: { id: CourseCategory; label: string; icon: string }[] = [
  { id: 'desarrollo-tecnico', label: 'Desarrollo técnico', icon: '💻' },
  { id: 'posicionamiento', label: 'Posicionamiento de perfil', icon: '🎯' },
  { id: 'practica', label: 'Práctica de exámenes', icon: '📝' },
];

export const bootcampCategories: { id: CourseCategory; label: string; icon: string }[] = [
  { id: 'bootcamp', label: 'Bootcamp', icon: '🚀' },
];

export interface Course {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  category: CourseCategory;
  level: string;
  badge: string;
  badgeColor: 'green' | 'yellow' | 'purple' | 'blue';
  weeks: string;
  totalClasses: number;
  totalModules: number;
  gradient: string;
  iconText: string;
  tags: string[];
  skills: string[];
  tools: string[];
  prerequisites: string[];
  modules: CourseModule[];
  videoPlaceholder?: string;
  contentLink?: string;
}

export const courses: Course[] = [
  {
    id: 'sql',
    title: 'SQL para Data Engineering',
    shortTitle: 'SQL',
    description: 'Desde SELECT hasta Window Functions, CTEs, JOINs y UNION. 30 ejercicios progresivos con teoría, práctica y solución en un editor SQL interactivo. El SQL que necesitás para entrevistas técnicas y tu día a día como Data Engineer.',
    category: 'desarrollo-tecnico',
    level: 'Fundamental',
    badge: 'Popular',
    badgeColor: 'yellow',
    weeks: 'Semanas 1-2',
    totalClasses: 30,
    totalModules: 6,
    gradient: 'from-green-900/40 via-green-800/20 to-emerald-600/30',
    iconText: 'SQL',
    tags: ['SELECT', 'JOINs', 'CTEs', 'Window Functions', 'GROUP BY', 'CASE WHEN', 'UNION'],
    skills: [
      'SELECT, WHERE, ORDER BY y LIMIT',
      'Funciones de agregación: COUNT, SUM, AVG',
      'GROUP BY y HAVING para análisis agrupado',
      'CASE WHEN para clasificación y segmentación',
      'CTEs (WITH) para queries limpios y reutilizables',
      'JOINs: INNER, LEFT, múltiples tablas',
      'UNION y UNION ALL para combinar resultados',
      'Window Functions: ROW_NUMBER y LAG',
    ],
    tools: [],
    prerequisites: [
      'No se requiere experiencia previa en SQL',
      'Ganas de aprender y practicar',
    ],
    modules: [
      { title: 'SQL Esencial', topics: ['SELECT y ORDER BY', 'WHERE y filtros', 'LIMIT y TOP N', 'Expresiones y alias (AS)'] },
      { title: 'Agregaciones', topics: ['COUNT y SUM', 'GROUP BY', 'HAVING'] },
      { title: 'Lógica Condicional', topics: ['CASE WHEN para clasificación', 'Segmentación con CASE', 'Funciones de fecha (TIMESTAMPDIFF)'] },
      { title: 'CTEs y JOINs Básicos', topics: ['CTEs (WITH)', 'INNER JOIN con CTE', 'LEFT JOIN con IS NULL'] },
      { title: 'JOINs Avanzados', topics: ['JOIN simple entre tablas', 'LEFT JOIN con GROUP BY', 'Múltiples JOINs y CONCAT'] },
      { title: 'SQL Avanzado', topics: ['CTEs múltiples', 'COALESCE y CROSS JOIN', 'UNION y UNION ALL', 'ROW_NUMBER()', 'LAG() y Window Functions'] },
    ],
  },
  {
    id: 'fundamentos-de',
    title: 'Fundamentos de Data Engineering',
    shortTitle: 'Data Eng',
    description: 'Los conceptos fundamentales que todo Data Engineer necesita dominar: arquitecturas de datos, Medallion, modelado dimensional, estrategias de ingesta, idempotencia, SCDs, optimización y orquestación. 100% conceptual, agnóstico de tecnología.',
    category: 'desarrollo-tecnico',
    level: 'Intermedio',
    badge: 'Core',
    badgeColor: 'green',
    weeks: '5 semanas',
    totalClasses: 22,
    totalModules: 5,
    gradient: 'from-purple-900/40 via-purple-800/20 to-violet-600/30',
    iconText: 'DE',
    tags: ['Medallion', 'Star Schema', 'MERGE', 'Idempotencia', 'ACID', 'SCD', 'Orquestación'],
    skills: [
      'Arquitectura Medallion end-to-end',
      'Modelado dimensional (Star & Snowflake)',
      'MERGE, upserts e idempotencia',
      'Propiedades ACID, Time Travel y retención de datos',
      'SCDs Type 1 y Type 2',
      'Estrategias de optimización: particionamiento, ordering, clustering',
      'Orquestación de pipelines y workflows',
      'Diseño de proyecto end-to-end con defensa técnica',
    ],
    tools: [],
    prerequisites: [
      'Conocimientos básicos de bases de datos',
      'Interés en arquitectura de datos',
    ],
    modules: [
      // Semana 1 — ¿Qué es Data Engineering? Pensar en datos
      { title: 'Semana 1: Fundamentos y ecosistema', topics: ['¿Qué hace un Data Engineer?', 'Data Engineer vs otros roles de datos', 'Pensar en datos: fuentes, formatos, volumen', 'Data Warehouse: concepto y limitaciones', 'Data Lake: concepto y riesgo del Data Swamp', 'Lakehouse, ETL vs ELT, Batch vs Streaming'] },
      // Semana 2 — Arquitectura Medallion
      { title: 'Semana 2: Arquitectura Medallion', topics: ['Medallion: el concepto y los principios', 'Capa Bronze: datos crudos e inmutables', 'Capa Silver: transformaciones y validaciones', 'Capa Gold: datos de negocio y KPIs', 'Buenas prácticas, documentación y linaje'] },
      // Semana 3 — Modelado Dimensional
      { title: 'Semana 3: Modelado Dimensional', topics: ['Tablas de hechos (Facts): grano y tipos', 'Tablas de dimensiones: claves subrogadas y dim_tiempo', 'Star Schema vs Snowflake Schema'] },
      // Semana 4 — MERGE, Idempotencia y SCDs
      { title: 'Semana 4: MERGE, Idempotencia y SCDs', topics: ['MERGE y Upsert', 'Idempotencia: el concepto más importante', 'SCD Type 1: sobrescribir sin historia', 'SCD Type 2: historial completo'] },
      // Semana 5 — ACID, Optimización y Proyecto
      { title: 'Semana 5: ACID, Optimización y Proyecto', topics: ['Propiedades ACID y Time Travel', 'Estrategias de optimización', 'Orquestación de pipelines', 'Diseño de proyecto end-to-end'] },
    ],
  },
  {
    id: 'python-intro',
    title: 'Introducción a Python',
    shortTitle: 'Python Intro',
    description: 'Desde cero hasta funciones y estructuras de datos. Variables, condicionales, ciclos, funciones, listas, diccionarios y manejo de errores. 38 ejercicios progresivos con teoría y práctica en un editor interactivo.',
    category: 'desarrollo-tecnico',
    level: 'Fundamental',
    badge: 'Nuevo',
    badgeColor: 'blue',
    weeks: 'Semana 3',
    totalClasses: 38,
    totalModules: 3,
    gradient: 'from-blue-900/40 via-blue-800/20 to-cyan-600/30',
    iconText: 'PY',
    tags: ['Variables', 'Funciones', 'Listas', 'Diccionarios', 'try/except'],
    skills: [
      'Variables, tipos básicos y operadores',
      'Condicionales, ciclos y control de flujo',
      'Funciones, parámetros y retornos',
      'Listas, tuplas, sets y diccionarios',
      'Manejo de errores con try/except',
    ],
    tools: [],
    prerequisites: [
      'No se requiere experiencia previa en Python',
      'Ganas de aprender y practicar',
    ],
    modules: [
      { title: 'Introducción', topics: ['Variables y tipos básicos', 'Operadores y expresiones', 'Print y salida', 'Condicionales', 'Ciclos', 'Números', 'Strings', 'Listas básicas'] },
      { title: 'Estructuras y Funciones', topics: ['Definición de funciones', 'Parámetros y retornos', 'Funciones con lógica', 'Funciones de orden superior'] },
      { title: 'Contenedores y Errores', topics: ['Tuplas', 'Sets', 'Diccionarios', 'Manejo de errores (try/except)'] },
    ],
  },
  {
    id: 'python-intermedio',
    title: 'Python Intermedio',
    shortTitle: 'Python Mid',
    description: 'Procesamiento de datos, algoritmos, list comprehensions y aleatoriedad. 30 ejercicios donde aprendés a manipular colecciones, pensar algorítmicamente y generar datos de prueba.',
    category: 'desarrollo-tecnico',
    level: 'Intermedio',
    badge: 'Nuevo',
    badgeColor: 'green',
    weeks: 'Semana 4',
    totalClasses: 30,
    totalModules: 3,
    gradient: 'from-green-900/40 via-emerald-800/20 to-teal-600/30',
    iconText: 'PY',
    tags: ['Datos', 'Algoritmos', 'Comprehensions', 'Random', 'Filtrado'],
    skills: [
      'Procesamiento y transformación de datos',
      'Filtrado y operaciones con colecciones',
      'List comprehensions',
      'Algoritmos de búsqueda y ordenamiento',
      'Números aleatorios y datos de prueba',
    ],
    tools: [],
    prerequisites: [
      'Introducción a Python completado (o equivalente)',
      'Funciones, listas y diccionarios',
    ],
    modules: [
      { title: 'Trabajando con Datos', topics: ['Procesamiento de datos', 'Filtrado y transformación', 'Operaciones con colecciones'] },
      { title: 'Algoritmos y Listas', topics: ['List comprehensions', 'Algoritmos de búsqueda', 'Algoritmos de ordenamiento'] },
      { title: 'Aleatoriedad', topics: ['Random y números aleatorios', 'Simulaciones y probabilidades', 'Datos de prueba y reproducibilidad'] },
    ],
  },
  {
    id: 'python-avanzado',
    title: 'Python Avanzado',
    shortTitle: 'Python Adv',
    description: 'Organización profesional del código, OOP, manejo de archivos y fechas. 38 ejercicios que te llevan de "saber Python" a escribir código mantenible en un equipo real.',
    category: 'desarrollo-tecnico',
    level: 'Avanzado',
    badge: 'Nuevo',
    badgeColor: 'purple',
    weeks: 'Semana 5',
    totalClasses: 38,
    totalModules: 4,
    gradient: 'from-purple-900/40 via-violet-800/20 to-fuchsia-600/30',
    iconText: 'PY',
    tags: ['OOP', 'Clases', 'Herencia', 'Archivos', 'Datetime', 'Modularización'],
    skills: [
      'Organización de scripts y modularización',
      'Documentación y buenas prácticas',
      'Datetime, archivos y rutas',
      'Clases, objetos y herencia (OOP)',
      'Estructuras de datos: pilas y colas',
    ],
    tools: [],
    prerequisites: [
      'Python Intermedio completado (o equivalente)',
      'Dominio de funciones, colecciones y algoritmos',
    ],
    modules: [
      { title: 'Complejidad y Organización', topics: ['Búsqueda binaria', 'Organización de scripts', 'Modularización'] },
      { title: 'Diseño y Especificación', topics: ['Documentación', 'Docstrings', 'Buenas prácticas'] },
      { title: 'Fechas y Carpetas', topics: ['Datetime', 'Manejo de archivos', 'Rutas y directorios'] },
      { title: 'Clases y Objetos', topics: ['Clases y constructores', 'Métodos y atributos', 'Herencia', 'Métodos especiales', 'Estructuras de datos (Pilas, Colas)'] },
    ],
  },
  {
    id: 'linkedin',
    title: 'LinkedIn: Posicionamiento de Perfil',
    shortTitle: 'LinkedIn',
    description: 'Cómo armar tu perfil de LinkedIn para posicionarte como profesional en Data. Headline, About, experiencia, proyectos y estrategia de contenido. Lo que los reclutadores realmente miran.',
    category: 'posicionamiento',
    level: 'Todos los niveles',
    badge: 'Bonus',
    badgeColor: 'purple',
    weeks: 'Módulo extra',
    totalClasses: 8,
    totalModules: 4,
    gradient: 'from-sky-900/40 via-sky-800/20 to-blue-600/30',
    iconText: 'in',
    tags: ['Perfil optimizado', 'Networking', 'Marca personal', 'Reclutadores', 'Contenido'],
    skills: [
      'Perfil optimizado para recruiters de Data',
      'Headline que posiciona y diferencia',
      'Storytelling técnico para el About',
      'Estrategia de contenido sostenible',
      'Networking efectivo sin ser spam',
    ],
    tools: [
      'LinkedIn (perfil personal)',
      'Plantillas de headline y about',
      'Checklist de optimización',
    ],
    prerequisites: [
      'Tener cuenta de LinkedIn',
      'Cualquier nivel técnico',
    ],
    modules: [
      { title: 'Fundamentos del Perfil', topics: ['Por qué LinkedIn importa en Data', 'Headline: fórmula para Data Engineers'] },
      { title: 'Secciones clave', topics: ['About: storytelling técnico', 'Experiencia: cómo redactar logros con impacto', 'Proyectos y certificaciones relevantes'] },
      { title: 'Estrategia de contenido', topics: ['Qué publicar y con qué frecuencia', 'Formatos que funcionan (carrusel, post técnico, historia)'] },
      { title: 'Networking & Visibilidad', topics: ['Cómo conectar con reclutadores', 'Marca personal en el ecosistema Data'] },
    ],
  },
  {
    id: 'examenes-python',
    title: 'Práctica de Exámenes Python',
    shortTitle: 'Exámenes Python',
    description: 'Ejercicios de práctica tipo examen para Python. Desde funciones básicas hasta algoritmos avanzados, cada ejercicio tiene validación estricta con casos de prueba. Ideal para prepararte para entrevistas técnicas.',
    category: 'practica',
    level: 'Intermedio-Avanzado',
    badge: 'Examen',
    badgeColor: 'yellow',
    weeks: 'Práctica continua',
    totalClasses: 20,
    totalModules: 1,
    gradient: 'from-yellow-900/40 via-orange-800/20 to-red-600/30',
    iconText: 'EX',
    tags: ['Funciones', 'Algoritmos', 'Recursión', 'Listas', 'Diccionarios', 'Strings', 'Matrices'],
    skills: [
      'Resolver ejercicios de entrevistas técnicas de Python',
      'Implementar funciones con validación estricta',
      'Algoritmos: factorial, palíndromo, Fibonacci',
      'Manipulación de listas, strings y diccionarios',
      'Recursión y programación funcional',
      'Trabajo con matrices y estructuras complejas',
    ],
    tools: [],
    prerequisites: [
      'Python Intermedio completado (o conocimientos equivalentes)',
      'Familiaridad con funciones, listas y diccionarios',
    ],
    modules: [
      { title: 'Exámenes de Práctica', topics: ['Suma y Factorial (básico)', 'Palíndromo y Filtrado (básico-intermedio)', 'Conteo de palabras y Ordenamiento (intermedio)', 'Fibonacci y Números primos (intermedio)', 'Máximo en lista y Aplanamiento (intermedio)', 'Cifrado César, Matrices y más (avanzado-examen)'] },
    ],
  },
  {
    id: 'examenes-sql',
    title: 'Práctica de Exámenes SQL',
    shortTitle: 'Exámenes SQL',
    description: 'Simulacros de exámenes técnicos de SQL con un modelo relacional real. Resolvé ejercicios de entrevistas técnicas con el esquema de base de datos visible, como en un examen real. Dificultad intermedia a avanzada.',
    category: 'practica',
    level: 'Intermedio-Avanzado',
    badge: 'Examen',
    badgeColor: 'yellow',
    weeks: 'Práctica continua',
    totalClasses: 6,
    totalModules: 1,
    gradient: 'from-red-900/40 via-orange-800/20 to-yellow-600/30',
    iconText: 'EX',
    tags: ['JOINs', 'GROUP BY', 'HAVING', 'CASE WHEN', 'CTEs', 'Window Functions', 'Subqueries'],
    skills: [
      'Resolver ejercicios de entrevistas técnicas bajo presión',
      'JOINs múltiples entre tablas relacionadas',
      'Agregaciones con GROUP BY y HAVING',
      'CASE WHEN para categorización de datos',
      'CTEs y Window Functions (RANK, ROW_NUMBER)',
      'Interpretar un DER y escribir queries correctas',
    ],
    tools: [],
    prerequisites: [
      'SQL para Data Engineering completado (o conocimientos equivalentes)',
      'Familiaridad con JOINs, GROUP BY y subqueries',
    ],
    modules: [
      { title: 'Examen Técnico — Modelo Clásico', topics: ['Países de clientes (DISTINCT, ORDER BY)', 'Empleados sin reportes (NOT EXISTS)', 'Empleados por oficina (JOIN, GROUP BY, HAVING)', 'Costo por categoría (CASE WHEN, múltiples JOINs)', 'Top 10 productos (CTEs, Window Functions)', 'Filtrado con LIKE y COUNT'] },
    ],
  },
  {
    id: 'presentacion-proyectos',
    title: 'Cómo Presentar un Proyecto',
    shortTitle: 'Proyecto',
    description: 'Lineamientos para presentar un proyecto de Data Engineering de forma profesional. Cómo armar el repo en GitHub, grabar un video de presentación, publicar en LinkedIn y comunicar tu valor diferencial. Lo que convierte un proyecto técnico en una carta de presentación.',
    category: 'posicionamiento',
    level: 'Todos los niveles',
    badge: 'Bonus',
    badgeColor: 'purple',
    weeks: 'Módulo extra',
    totalClasses: 15,
    totalModules: 4,
    gradient: 'from-amber-900/40 via-orange-800/20 to-yellow-600/30',
    iconText: '🚀',
    tags: ['GitHub', 'README', 'Video', 'LinkedIn', 'Portfolio', 'Marca personal'],
    skills: [
      'Armar un repositorio profesional en GitHub para cualquier proyecto de Data Engineering',
      'Escribir un README claro con capturas y arquitectura',
      'Grabar un video de presentación de 5-10 minutos',
      'Estructurar la explicación: problema, arquitectura, insights, resultados',
      'Publicar un proyecto en LinkedIn para recruiters',
      'Comunicar valor diferencial con historia personal',
      'Convertir challenges de entrevistas en proyectos de portfolio',
    ],
    tools: [
      'GitHub (repositorio y README)',
      'Herramienta de grabación (Loom, OBS, o cámara del celular)',
      'LinkedIn (publicación y perfil)',
    ],
    prerequisites: [
      'Tener un proyecto de Data Engineering armado o en progreso (de cualquier fuente)',
      'Cuenta de GitHub',
      'Cualquier nivel técnico',
    ],
    contentLink: '/cursos/presentacion-proyectos/guia',
    modules: [
      { title: 'Repositorio en GitHub', topics: ['Por qué subir el proyecto a GitHub', 'README con Markdown: qué incluir y qué no', 'Capturas de dashboards y visualizaciones', 'Mostrar la arquitectura (Bronze/Silver/Gold, APIs, modelado)', 'Overview personal y preguntas de negocio'] },
      { title: 'Video de presentación', topics: ['Por qué hacer un video (iniciativa, creatividad, comunicación)', 'Duración ideal: 5-10 minutos', 'Estructura: problema, arquitectura, insights, resultados', 'Con cara o al menos con voz', 'Referencia: papers académicos y videos de 5 minutos'] },
      { title: 'LinkedIn y difusión', topics: ['Cómo publicar el proyecto en LinkedIn', 'Qué mostrar para que un recruiter te contacte', 'Links entre repo, video y perfil'] },
      { title: 'Tu diferencial personal', topics: ['Tu historia como valor agregado', 'Elegir proyectos que te apasionen', 'Challenges de entrevistas como portfolio', 'La mejor forma de vender es sin vender'] },
    ],
  },

  // ─── Bootcamps ─────────────────────────────────────────────────
  {
    id: 'bootcamp-de-foundations',
    title: 'Data Engineering Foundations',
    shortTitle: 'DE Foundations',
    description: 'Bootcamp intensivo de 10 semanas: SQL, Python, Spark, Databricks, Medallion Architecture y proyecto final con defensa. De cero a Data Engineer con clases grabadas, ejercicios descargables y examen semanal.',
    category: 'bootcamp',
    level: 'De cero a intermedio-avanzado',
    badge: '2da Edicion',
    badgeColor: 'green',
    weeks: '10 semanas',
    totalClasses: 10,
    totalModules: 10,
    gradient: 'from-emerald-900/40 via-green-800/20 to-cyan-600/30',
    iconText: 'DE',
    tags: ['SQL', 'Python', 'Spark', 'Databricks', 'Medallion', 'AWS', 'Proyecto final'],
    skills: [
      'SQL desde cero hasta Window Functions en Databricks',
      'Python para procesamiento de datos y PySpark',
      'Spark: DataFrames, transformaciones y pipelines',
      'Arquitectura Medallion end-to-end',
      'Modelado dimensional: Star Schema, SCDs',
      'Orquestacion, MERGE e idempotencia',
      'Proyecto integrador con defensa tecnica',
    ],
    tools: ['Databricks', 'PySpark', 'SQL', 'Python', 'Delta Lake', 'GitHub'],
    prerequisites: [
      'Ganas de hacer la transicion a Data Engineering',
      'Conocimientos basicos de logica (no se requiere experiencia previa)',
    ],
    modules: [
      { title: 'Semana 1 — Fundamentos, Databricks y SQL Base', topics: ['Intro DE + Por qué Databricks', 'Setup Databricks + Primeras queries', 'SQL Base: JOINs, Agregaciones, GROUP BY'] },
      { title: 'Semana 2 — GitHub, CTEs, Window Functions y EDA', topics: ['GitHub + Control de versiones', 'CTEs + Window Functions (ROW_NUMBER, LAG, LEAD)', 'EDA completo con SQL'] },
      { title: 'Semana 3 — Arquitectura Medallion y Delta Lake', topics: ['Arquitectura Medallion: Bronze, Silver, Gold', 'Delta Lake: ACID, Time Travel, formatos', 'Pipeline Bronze → Silver'] },
      { title: 'Semana 4 — Transformación Silver, Esquemas e Inserción', topics: ['Transformación Silver: limpieza y deduplicación', 'Star Schema vs Snowflake vs OBT', 'INSERT INTO, OVERWRITE y MERGE'] },
      { title: 'Semana 5 — Facts, Dimensions y SCDs', topics: ['Facts vs Dims + claves subrogadas', 'Creación de dimensiones y fact tables', 'SCD Type 1 y Type 2'] },
      { title: 'Semana 6 — PySpark, Python e Ingesta de APIs', topics: ['PySpark: filter, groupBy, joins', 'Lectura/escritura de archivos y Delta', 'Ingesta de APIs y desanidamiento de JSON'] },
      { title: 'Semana 7 — Marca Personal para Data Engineers', topics: ['LinkedIn: headline, About y STAR Framework', 'Networking: tu red como grafo y efecto multiplicador', 'Contenido: Hook-Script-CTA y batch creation'] },
      { title: 'Semana 8 — Workflows, Dashboards y Cierre Técnico', topics: ['Workflows: orquestación y scheduling', 'Dashboards integrados en Databricks', 'Python vs PySpark vs Spark SQL'] },
      { title: 'Semana 9 — Pipeline End-to-End y Proyecto Final', topics: ['Pipeline completo integrando todo', 'Repaso de conceptos clave', 'Definición de scope y dataset del proyecto'] },
      { title: 'Semana 10 — Proyecto Final: Pipeline End-to-End y Presentación', topics: ['Walkthrough pipeline completo: repo, DDLs, ETLs, views, dashboards', 'Workflows, idempotencia y estrategias de escritura', 'Cómo presentar: GitHub, README, video demo y LinkedIn'] },
    ],
  },
];

export const getCourseById = (id: string): Course | undefined => courses.find(c => c.id === id);
